if (!customElements.get('brand-logo-strip')) {
  customElements.define('brand-logo-strip', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.viewport = this.querySelector('[data-logo-viewport]');
      if (!this.viewport) return;
      this.track = this.querySelector('[data-logo-track]');
      this.original = this.querySelector('[data-logo-original]');
      this.toggle = this.querySelector('[data-logo-toggle]');
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.reasons = new Set();
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.fineHover = matchMedia('(hover: hover) and (pointer: fine)');
      this.visible = true;
      this.userPaused = false;
      this.onMotion = () => this.rebuild();
      this.motion.addEventListener('change', this.onMotion);
      this.toggle.addEventListener('click', () => {
        this.userPaused = !this.userPaused;
        this.toggle.setAttribute('aria-pressed', String(this.userPaused));
        this.toggle.setAttribute('aria-label', `${this.userPaused ? 'Resume' : 'Pause'} brand logo movement`);
        this.toggle.querySelector('[data-logo-toggle-icon]').textContent = this.userPaused ? '▶' : 'Ⅱ';
        this.sync();
      }, { signal });
      this.addEventListener('pointerenter', event => {
        if (event.pointerType === 'mouse' && this.dataset.hoverPause === 'true' && this.fineHover.matches) this.pause('hover', true);
      }, { signal });
      this.addEventListener('pointerleave', () => this.pause('hover', false), { signal });
      this.addEventListener('focusin', () => this.pause('focus', true), { signal });
      this.addEventListener('focusout', () => queueMicrotask(() => {
        if (this.isConnected) this.pause('focus', this.contains(document.activeElement));
      }), { signal });
      this.viewport.addEventListener('pointerdown', () => { this.pointerDown = true; this.pause('interaction', true); }, { signal, passive: true });
      const release = () => { if (this.pointerDown) { this.pointerDown = false; this.holdInteraction(); } };
      window.addEventListener('pointerup', release, { signal, passive: true });
      window.addEventListener('pointercancel', release, { signal, passive: true });
      this.viewport.addEventListener('wheel', () => this.holdInteraction(), { signal, passive: true });
      this.viewport.addEventListener('scroll', () => {
        if (this.reasons.has('interaction') && !this.pointerDown) this.holdInteraction();
      }, { signal, passive: true });
      document.addEventListener('visibilitychange', () => this.sync(), { signal });
      for (const eventName of ['shopify:section:select', 'shopify:section:deselect', 'shopify:block:select', 'shopify:block:deselect']) {
        document.addEventListener(eventName, event => {
          if (event.detail?.sectionId !== this.dataset.sectionId && !this.contains(event.target)) return;
          const selecting = eventName.endsWith(':select');
          this.pause(eventName.includes(':block:') ? 'editor-block' : 'editor-section', selecting);
          if (selecting && event.detail?.blockId) {
            const item = [...this.original.children].find(node => node.dataset.logoBlock === event.detail.blockId);
            if (item) this.viewport.scrollLeft = item.offsetLeft - this.original.offsetLeft;
          }
        }, { signal });
      }
      document.addEventListener('shopify:section:unload', event => {
        if (event.detail?.sectionId === this.dataset.sectionId) this.disconnectedCallback();
      }, { signal });
      this.resize = new ResizeObserver(() => this.rebuild());
      this.resize.observe(this.viewport);
      this.intersection = new IntersectionObserver(entries => {
        this.visible = entries[0].isIntersecting;
        this.sync();
      });
      this.intersection.observe(this);
      this.rebuild();
    }

    rebuild() {
      if (!this.controller) return;
      cancelAnimationFrame(this.frame);
      this.frame = null;
      this.track.querySelectorAll('[data-logo-copy]').forEach(node => node.remove());
      this.automatic = this.dataset.auto === 'true' && !this.motion.matches && this.viewport.clientWidth > 0;
      this.toggle.hidden = !this.automatic;
      this.toggleAttribute('data-running-layout', this.automatic);
      this.cycle = this.original.getBoundingClientRect().width + parseFloat(getComputedStyle(this.track).columnGap || 0);
      if (this.automatic && this.cycle > 0) {
        const copies = Math.ceil(this.viewport.clientWidth / this.cycle) + 2;
        for (let i = 0; i < copies; i++) {
          const copy = this.original.cloneNode(true);
          copy.removeAttribute('data-logo-original');
          copy.setAttribute('data-logo-copy', '');
          copy.setAttribute('aria-hidden', 'true');
          for (const node of [copy, ...copy.querySelectorAll('*')]) {
            node.removeAttribute('id');
            for (const attr of [...node.attributes]) if (attr.name.startsWith('data-shopify') || attr.name === 'data-logo-block') node.removeAttribute(attr.name);
            if (node.matches('a,button,input,select,textarea,[tabindex]')) node.setAttribute('tabindex', '-1');
          }
          this.track.append(copy);
        }
        this.position = this.dataset.direction === 'right' ? this.cycle : 0;
        this.viewport.scrollLeft = this.position;
      } else {
        this.viewport.scrollLeft = 0;
      }
      this.sync();
    }

    pause(reason, active) {
      if (active) this.reasons.add(reason); else this.reasons.delete(reason);
      this.sync();
    }

    holdInteraction() {
      this.pause('interaction', true);
      clearTimeout(this.interactionTimer);
      this.interactionTimer = setTimeout(() => this.pause('interaction', false), 1200);
    }

    sync() {
      if (!this.controller) return;
      const run = this.automatic && !this.userPaused && !this.reasons.size && this.visible && !document.hidden;
      if (!run) {
        cancelAnimationFrame(this.frame);
        this.frame = null;
        this.lastTime = null;
        return;
      }
      if (this.frame != null) return;
      this.position = this.viewport.scrollLeft;
      const tick = time => {
        const elapsed = this.lastTime == null ? 0 : Math.min(time - this.lastTime, 64) / 1000;
        this.lastTime = time;
        const direction = this.dataset.direction === 'right' ? -1 : 1;
        this.position += direction * Number(this.dataset.speed || 20) * elapsed;
        if (this.cycle > 0) this.position = ((this.position % this.cycle) + this.cycle) % this.cycle;
        this.viewport.scrollLeft = this.position;
        this.frame = requestAnimationFrame(tick);
      };
      this.frame = requestAnimationFrame(tick);
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      cancelAnimationFrame(this.frame);
      this.frame = null;
      this.lastTime = null;
      clearTimeout(this.interactionTimer);
      this.motion?.removeEventListener('change', this.onMotion);
      this.resize?.disconnect();
      this.intersection?.disconnect();
      this.track?.querySelectorAll('[data-logo-copy]').forEach(node => node.remove());
    }
  });
}
